import type {
  CookieCategory,
  IConsentPreferences,
  IConsentRecord,
} from '../api/types'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import {
  BACKEND_SYNC_ENABLED,
  fetchConsentPreferences,
  updateConsentPreferences,
} from '../api/consentApi'
import {
  CATEGORY_ORDER,
  CONSENT_SCHEMA_VERSION,
  CONSENT_STORAGE_KEY,
  DEFAULT_PREFERENCES,
  FULL_CONSENT_PREFERENCES,
  LEGACY_ACCEPTED_VALUE,
  LEGACY_CONSENT_KEY,
} from './constants'

/**
 * Central authority on the user's cookie-consent state.
 *
 * Persistence model:
 *   - Primary: localStorage (per-device, survives reloads)
 *   - Secondary (gated by {@link BACKEND_SYNC_ENABLED}): backend, so
 *     preferences follow an authenticated user across devices.
 *
 * GDPR responsibilities this store owns:
 *   - Art. 7(1): prove consent — we persist `decidedAt` and the exact
 *     category set that was agreed to.
 *   - Art. 7(3): withdrawal must be as easy as granting. Every mutation
 *     that sets consent has a mirror that removes it; `reset()` wipes
 *     the decision entirely so the banner reappears.
 *   - Art. 7(4): no pre-ticked boxes. {@link DEFAULT_PREFERENCES} keeps
 *     non-essential categories off until the user turns them on.
 */
export const useConsentStore = defineStore('consent', () => {
  // `null` === user has not made a decision yet → banner must be shown.
  const record = ref<IConsentRecord | null>(null)

  // Used to avoid hydrating twice in development under HMR / StrictMode.
  const isInitialized = ref(false)

  const hasDecided = computed(() => record.value !== null)

  const preferences = computed<IConsentPreferences>(() => {
    return record.value?.preferences ?? DEFAULT_PREFERENCES
  })

  const decidedAt = computed(() => record.value?.decidedAt ?? null)

  function isCategoryGranted(category: CookieCategory): boolean {
    return preferences.value[category] === true
  }

  /**
   * Read persisted state once per app lifecycle. If the legacy v1 key
   * is present, migrate it into the granular record and clear the old
   * key — we want a single source of truth.
   */
  async function initialize(): Promise<void> {
    if (isInitialized.value)
      return
    isInitialized.value = true

    const fromStorage = readFromStorage()
    if (fromStorage) {
      record.value = fromStorage
    }
    else {
      const migrated = migrateLegacyRecord()
      if (migrated) {
        record.value = migrated
        writeToStorage(migrated)
      }
    }

    if (BACKEND_SYNC_ENABLED) {
      try {
        const remote = await fetchConsentPreferences()
        if (remote)
          applyRemotePreferences(remote)
      }
      catch (error) {
        // Swallow — a failed sync must never block the local experience.
        console.warn('[consent] remote fetch failed', error)
      }
    }
  }

  /**
   * Grant consent to every category. Writes synchronously to localStorage
   * and kicks off a best-effort backend sync.
   */
  function acceptAll(): void {
    persist({ ...FULL_CONSENT_PREFERENCES })
  }

  /**
   * Deny consent to every non-essential category. This is the Art. 7(3)
   * "withdraw as easily as granted" path.
   */
  function rejectAll(): void {
    persist({ ...DEFAULT_PREFERENCES })
  }

  /**
   * Apply a partial change and persist. Only non-necessary categories
   * can be modified — the `necessary: true` invariant is enforced here.
   */
  function update(partial: Partial<Omit<IConsentPreferences, 'necessary'>>): void {
    const next: IConsentPreferences = {
      ...preferences.value,
      ...partial,
      necessary: true,
    }
    persist(next)
  }

  /**
   * Wipe the decision. Banner reappears on next load. Used both by the
   * "withdraw all" affordance and by logout flows that want to reset
   * per-user state on a shared device.
   */
  function reset(): void {
    record.value = null
    removeFromStorage()
    if (BACKEND_SYNC_ENABLED) {
      // Sending defaults (all false) is the closest remote analogue
      // of "forgotten". We cannot truly delete server-side from here.
      updateConsentPreferences({
        preferences: { ...DEFAULT_PREFERENCES },
        decidedAt: new Date().toISOString(),
      }).catch(() => {})
    }
  }

  /**
   * Core write path used by all mutations. Keeps localStorage and the
   * in-memory record in lockstep and fires off backend sync.
   */
  function persist(next: IConsentPreferences): void {
    const nextRecord: IConsentRecord = {
      preferences: next,
      decidedAt: new Date().toISOString(),
      version: CONSENT_SCHEMA_VERSION,
    }
    record.value = nextRecord
    writeToStorage(nextRecord)

    if (BACKEND_SYNC_ENABLED) {
      updateConsentPreferences({
        preferences: nextRecord.preferences,
        decidedAt: nextRecord.decidedAt,
      }).catch((error) => {
        console.warn('[consent] remote update failed', error)
      })
    }
  }

  function applyRemotePreferences(remote: IConsentPreferences): void {
    // Remote always wins when it exists — a user who set preferences
    // on another device expects to see them here.
    const next: IConsentRecord = {
      preferences: sanitizePreferences(remote),
      decidedAt: record.value?.decidedAt ?? new Date().toISOString(),
      version: CONSENT_SCHEMA_VERSION,
    }
    record.value = next
    writeToStorage(next)
  }

  return {
    // state
    record,
    // getters
    hasDecided,
    preferences,
    decidedAt,
    categories: CATEGORY_ORDER,
    // queries
    isCategoryGranted,
    // actions
    initialize,
    acceptAll,
    rejectAll,
    update,
    reset,
  }
})

// -- storage helpers -------------------------------------------------------

function readFromStorage(): IConsentRecord | null {
  try {
    const raw = localStorage.getItem(CONSENT_STORAGE_KEY)
    if (!raw)
      return null

    const parsed = JSON.parse(raw) as Partial<IConsentRecord>
    if (!parsed || parsed.version !== CONSENT_SCHEMA_VERSION)
      return null
    if (!parsed.preferences || typeof parsed.decidedAt !== 'string')
      return null

    return {
      preferences: sanitizePreferences(parsed.preferences),
      decidedAt: parsed.decidedAt,
      version: CONSENT_SCHEMA_VERSION,
    }
  }
  catch {
    return null
  }
}

function writeToStorage(record: IConsentRecord): void {
  try {
    localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(record))
  }
  catch (error) {
    console.warn('[consent] failed to write localStorage', error)
  }
}

function removeFromStorage(): void {
  try {
    localStorage.removeItem(CONSENT_STORAGE_KEY)
  }
  catch (error) {
    console.warn('[consent] failed to clear localStorage', error)
  }
}

/**
 * v1 stored only the string "accepted" — the user agreed to a generic
 * "we use cookies" message without category granularity. Honour that
 * broad agreement by migrating to "all accepted" rather than forcing a
 * re-prompt, and drop the old key so we only ever read v2 going forward.
 */
function migrateLegacyRecord(): IConsentRecord | null {
  try {
    const legacy = localStorage.getItem(LEGACY_CONSENT_KEY)
    if (legacy !== LEGACY_ACCEPTED_VALUE)
      return null

    localStorage.removeItem(LEGACY_CONSENT_KEY)
    return {
      preferences: { ...FULL_CONSENT_PREFERENCES },
      decidedAt: new Date().toISOString(),
      version: CONSENT_SCHEMA_VERSION,
    }
  }
  catch {
    return null
  }
}

function sanitizePreferences(input: Partial<IConsentPreferences>): IConsentPreferences {
  return {
    necessary: true,
    functional: input.functional === true,
    analytics: input.analytics === true,
    marketing: input.marketing === true,
  }
}
